import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Delete16Regular, Dismiss16Regular, Search16Regular } from '@fluentui/react-icons';
import {
    deleteJournalAnalysisPrompt,
    fetchJournalAnalysisPrompts,
} from '../../redux/journalAnalysisSlice';
import { titleFromPromptText } from '../../utils/askAiChatMeta';
import styles from './askAi.module.css';

const AskAiSavedPromptsModal = ({ open, onClose, onUsePrompt }) => {
    const dispatch = useDispatch();
    const { savedPrompts } = useSelector(state => state.journalAnalysis || {});
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (open) {
            dispatch(fetchJournalAnalysisPrompts());
            setSearch('');
        }
    }, [open, dispatch]);

    const filtered = useMemo(() => {
        const list = Array.isArray(savedPrompts) ? savedPrompts : [];
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter(
            p =>
                (p.title || '').toLowerCase().includes(q) ||
                (p.prompt || '').toLowerCase().includes(q),
        );
    }, [savedPrompts, search]);

    const handleDelete = async promptId => {
        try {
            await dispatch(deleteJournalAnalysisPrompt(promptId)).unwrap();
            toast.success('Prompt deleted');
        } catch {
            toast.error('Could not delete prompt');
        }
    };

    if (!open) return null;

    return (
        <div className={styles.modalOverlay} role="presentation" onClick={onClose}>
            <div
                className={styles.promptsModal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="saved-prompts-modal-title"
                onClick={e => e.stopPropagation()}>
                <div className={styles.promptsModalHeader}>
                    <div>
                        <h2 id="saved-prompts-modal-title" className={styles.promptsModalTitle}>
                            Saved Prompts
                        </h2>
                        <p className={styles.promptsModalSubtitle}>Private to your account</p>
                    </div>
                    <button
                        type="button"
                        className={styles.promptsCloseIcon}
                        onClick={onClose}
                        aria-label="Close">
                        <Dismiss16Regular />
                    </button>
                </div>

                <div className={styles.promptsSearch}>
                    <Search16Regular />
                    <input
                        type="search"
                        placeholder="Search saved prompts…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        aria-label="Search saved prompts"
                    />
                </div>

                <div className={styles.promptsList}>
                    {!filtered.length ? (
                        <p className={styles.historyEmpty}>
                            No saved prompts yet. Bookmark a prompt from the composer to reuse it
                            later.
                        </p>
                    ) : (
                        filtered.map(prompt => (
                            <div key={prompt.id} className={styles.promptCard}>
                                <div className={styles.promptCardHeader}>
                                    <div className={styles.promptCardTitle}>
                                        {prompt.title?.trim() ||
                                            titleFromPromptText(prompt.prompt)}
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.promptDeleteBtn}
                                        aria-label="Delete saved prompt"
                                        title="Delete"
                                        onClick={() => void handleDelete(prompt.id)}>
                                        <Delete16Regular />
                                    </button>
                                </div>
                                <p className={styles.promptCardBody}>{prompt.prompt}</p>
                                <button
                                    type="button"
                                    className={styles.usePromptBtn}
                                    onClick={() => {
                                        onUsePrompt(prompt.prompt || '');
                                        onClose();
                                    }}>
                                    Use prompt
                                </button>
                            </div>
                        ))
                    )}
                </div>

                <div className={styles.promptsModalFooter}>
                    <span className={styles.promptsCount}>
                        {filtered.length} prompt{filtered.length === 1 ? '' : 's'}
                    </span>
                    <button type="button" className={styles.promptsCloseBtn} onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AskAiSavedPromptsModal;
